import type { ComputedRef, Ref } from 'vue'
import { useSeasonalWelcome } from '~/composables/ui/useSeasonalTheme'
import { useOnboarding } from '~/composables/onboarding/useOnboarding'
import { useWelcomeSplash } from '~/composables/ui/useWelcomeSplash'
import { useUserPreferences } from '~/composables/ui/useUserPreferences'
import { useOrganigrammeEditRouteAccess } from '~/composables/auth/useOrganigrammeEditRouteAccess'
import { useAppToolboxAvailability } from '~/composables/ui/useAppToolboxAvailability'
import { buildSupportRequestMailtoHref } from '~~/utils/supportContact'
import {
  applyOverlayMountState,
  areToolboxShortcutsEnabled,
  confirmAvatarModeOnce,
  getLocalDayKey,
  isAvatarModeReady,
  isLoggedInPrefsLoading,
  isNoticeReadyForOnboarding,
  refreshAvatarSettingsIfLoggedIn,
  shouldOpenAvatarModeGate,
  shouldShowNonProdEnvironmentNotice,
  shouldShowOnboardingRestart,
  startTrainingAndCloseCenter,
} from '~/composables/layout/appLayoutOverlaysHelpers'

type UseAppLayoutOverlaysParams = {
  loggedIn: Ref<boolean>
  roles: Ref<string[]>
  toolboxOpen: Ref<boolean>
  myAvatarSettings: Ref<{ isConfigured: boolean }>
  myAvatarSettingsReady: Ref<boolean>
  refreshMyAvatarSettings: () => Promise<void>
  updateMyAvatarSettings: (payload: {
    avatarDisplayMode: 'teams' | 'avatar' | 'initials'
    avatarConfig?: Record<string, unknown> | null
  }) => Promise<void>
  fullName: ComputedRef<string>
  myGraphPhotoUrl: ComputedRef<string | null>
  me: Ref<{ noticeUpToDate: boolean } | null>
}

export function useAppLayoutOverlays(params: UseAppLayoutOverlaysParams) {
  const { canEditOrganigramOnCurrentView } = useOrganigrammeEditRouteAccess()
  const { isToolboxUiBlocked } = useAppToolboxAvailability()
  const runtimeConfig = useRuntimeConfig()

  const shouldShowEnvironmentNotice = shouldShowNonProdEnvironmentNotice(runtimeConfig.public.appEnv)
  const betaNoticeCookie = useCookie<string | null>('beta_demo_ack_date', {
    maxAge: 60 * 60 * 24,
    sameSite: 'lax',
    path: '/',
  })
  const showBetaNotice = ref(false)

  const showSeasonalFooterLink = ref(false)
  const seasonalFooterShortLabel = ref('')
  const showTrainingCenter = ref(false)

  const {
    showSeasonalWelcome,
    decorPrefs,
    activeSeasonalEvent,
    deferSeasonalWelcomeAfterBeta,
    openSeasonalWelcomeModal,
    tryAutoOpenSeasonalWelcomeModal,
    onSeasonalWelcomeConfirm,
    applyLiveSeasonalDecorUpdates,
  } = useSeasonalWelcome()

  const {
    isWelcomeResolved,
    shouldShowWelcomeSplash,
    onWelcomeSplashFinished,
  } = useWelcomeSplash({ loggedIn: params.loggedIn })

  const { isLoading: isUserPrefsLoading, isReady: isUserPrefsReady } = useUserPreferences()
  const isLoadingPrefs = computed(() =>
    isLoggedInPrefsLoading(params.loggedIn.value, isUserPrefsLoading.value, isUserPrefsReady.value),
  )

  watch(
    () => params.loggedIn.value,
    async (isLoggedIn) => {
      await refreshAvatarSettingsIfLoggedIn(isLoggedIn, params.refreshMyAvatarSettings)
    },
    { immediate: true },
  )

  const isNoticeUpToDateForOnboarding = computed(() =>
    isNoticeReadyForOnboarding(params.loggedIn.value, params.me.value),
  )

  const isAvatarModeResolved = computed(() =>
    isAvatarModeReady(
      params.loggedIn.value,
      params.myAvatarSettingsReady.value,
      params.myAvatarSettings.value.isConfigured,
    ),
  )

  const shouldShowAvatarModeGate = computed(() =>
    shouldOpenAvatarModeGate(
      params.loggedIn.value,
      isWelcomeResolved.value,
      isNoticeUpToDateForOnboarding.value,
      params.myAvatarSettingsReady.value,
      params.myAvatarSettings.value.isConfigured,
    ),
  )

  const isSavingAvatarMode = ref(false)

  async function onConfirmAvatarMode(payload: {
    avatarDisplayMode: 'teams' | 'avatar' | 'initials'
    avatarConfig: Record<string, unknown> | null
  }): Promise<void> {
    await confirmAvatarModeOnce({
      isSavingAvatarMode,
      updateMyAvatarSettings: params.updateMyAvatarSettings,
      payload,
    })
  }

  const {
    startTrainingById,
    availableTrainings,
    launchBlockerReason,
    activeTourId,
    hasReadCapability,
  } = useOnboarding({
    loggedIn: params.loggedIn,
    roles: params.roles,
    canEditOrganigramOnCurrentView,
    toolboxOpen: params.toolboxOpen,
    isToolboxUiBlocked,
    isNoticeUpToDate: isNoticeUpToDateForOnboarding,
    isEnvironmentNoticeOpen: showBetaNotice,
    isSeasonalWelcomeOpen: showSeasonalWelcome,
    isWelcomeResolved,
    isAvatarModeResolved,
  })

  const isEditModeBlockedByOnboarding = computed(() => activeTourId.value === 'org-read-guided-pdl')

  const showOnboardingRestartButton = computed(() =>
    shouldShowOnboardingRestart(
      params.loggedIn.value,
      hasReadCapability.value,
      isToolboxUiBlocked.value,
      isWelcomeResolved.value,
    ),
  )

  const supportContactMailtoHref = buildSupportRequestMailtoHref()

  function acknowledgeBetaNotice(): void {
    betaNoticeCookie.value = getLocalDayKey()
    showBetaNotice.value = false
    void nextTick(() => {
      tryAutoOpenSeasonalWelcomeModal()
    })
  }

  function onOpenTrainingCenter(): void {
    showTrainingCenter.value = true
  }

  async function onStartTraining(payload: { tourId: string; mode: 'do' | 'redo' }): Promise<void> {
    await startTrainingAndCloseCenter({
      startTrainingById,
      showTrainingCenter,
      payload,
    })
  }

  function initOverlayStateOnMount(): void {
    applyOverlayMountState({
      shouldShowEnvironmentNotice,
      betaNoticeAckDay: betaNoticeCookie.value,
      deferSeasonalWelcomeAfterBeta,
      showBetaNotice,
      showSeasonalFooterLink,
      seasonalFooterShortLabel,
    })
  }

  const toolboxShortcutsEnabled = computed(() =>
    areToolboxShortcutsEnabled(isToolboxUiBlocked.value, showSeasonalWelcome.value),
  )

  return {
    showBetaNotice,
    shouldShowEnvironmentNotice,
    acknowledgeBetaNotice,
    showSeasonalWelcome,
    decorPrefs,
    activeSeasonalEvent,
    onSeasonalWelcomeConfirm,
    applyLiveSeasonalDecorUpdates,
    openSeasonalWelcomeModal,
    showSeasonalFooterLink,
    seasonalFooterShortLabel,
    showTrainingCenter,
    availableTrainings,
    launchBlockerReason,
    onStartTraining,
    shouldShowAvatarModeGate,
    isSavingAvatarMode,
    onConfirmAvatarMode,
    isWelcomeResolved,
    shouldShowWelcomeSplash,
    isLoadingPrefs,
    onWelcomeSplashFinished,
    isEditModeBlockedByOnboarding,
    showOnboardingRestartButton,
    onOpenTrainingCenter,
    supportContactMailtoHref,
    initOverlayStateOnMount,
    toolboxShortcutsEnabled,
  }
}
